import { prefersReducedMotion, throttle } from '@theme/utilities';

const SCROLL_EDGE_EPSILON = 2;

/**
 * Horizontal scroll controls for cart drawer Discover more / Recently viewed strips.
 */
class CartDrawerDiscoverScroll extends HTMLElement {
  /** @type {HTMLElement | null} */
  #track = null;

  /** @type {HTMLElement | null} */
  #arrows = null;

  /** @type {HTMLButtonElement | null} */
  #prev = null;

  /** @type {HTMLButtonElement | null} */
  #next = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    this.#init();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#track = null;
    this.#arrows = null;
    this.#prev = null;
    this.#next = null;
  }

  #init() {
    this.#abort?.abort();
    this.#resizeObserver?.disconnect();

    this.#track = this.querySelector('.cart-drawer-discover__track');
    this.#arrows = this.querySelector('.cart-drawer-discover__arrows');
    this.#prev = this.querySelector('[data-action="previous"]');
    this.#next = this.querySelector('[data-action="next"]');

    if (!(this.#track instanceof HTMLElement) || !(this.#arrows instanceof HTMLElement)) return;
    if (!(this.#prev instanceof HTMLButtonElement) || !(this.#next instanceof HTMLButtonElement)) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#prev.addEventListener('click', this.#onPrevious, { signal });
    this.#next.addEventListener('click', this.#onNext, { signal });
    this.#track.addEventListener('scroll', this.#onScroll, { signal });

    this.#track.querySelectorAll('img').forEach((img) => {
      if (!(img instanceof HTMLImageElement)) return;
      if (img.complete) return;
      img.addEventListener('load', () => this.#updateControls(), { signal, once: true });
    });

    this.#resizeObserver = new ResizeObserver(throttle(() => this.#updateControls(), 50));
    this.#resizeObserver.observe(this.#track);
    for (const card of this.#getCards()) {
      this.#resizeObserver.observe(card);
    }

    this.#scheduleControlUpdate();
  }

  #scheduleControlUpdate() {
    queueMicrotask(() => this.#updateControls());
    requestAnimationFrame(() => {
      this.#updateControls();
      requestAnimationFrame(() => this.#updateControls());
    });
  }

  #onScroll = throttle(() => {
    this.#updateControls();
  }, 50);

  #onPrevious = (event) => {
    event.preventDefault();
    const index = this.#getActiveIndex();
    if (index > 0) {
      this.#scrollToIndex(index - 1);
    }
  };

  #onNext = (event) => {
    event.preventDefault();
    const cards = this.#getCards();
    const index = this.#getActiveIndex();
    if (index < cards.length - 1) {
      this.#scrollToIndex(index + 1);
    }
  };

  /**
   * @param {number} index
   */
  #scrollToIndex(index) {
    const track = this.#track;
    const cards = this.#getCards();
    if (!(track instanceof HTMLElement) || !cards.length) return;

    const card = cards[Math.max(0, Math.min(index, cards.length - 1))];
    track.scrollTo({ left: this.#getCardScrollOffset(card), behavior: this.#scrollBehavior() });
    queueMicrotask(() => this.#updateControls());
  }

  /**
   * @returns {number}
   */
  #getActiveIndex() {
    const track = this.#track;
    const cards = this.#getCards();
    if (!(track instanceof HTMLElement) || !cards.length) return 0;

    const { scrollLeft } = track;
    let activeIndex = 0;

    for (let i = 0; i < cards.length; i++) {
      if (this.#getCardScrollOffset(cards[i]) <= scrollLeft + SCROLL_EDGE_EPSILON) {
        activeIndex = i;
      }
    }

    return activeIndex;
  }

  /**
   * @param {HTMLElement} card
   * @returns {number}
   */
  #getCardScrollOffset(card) {
    const track = this.#track;
    if (!(track instanceof HTMLElement)) return 0;

    return card.getBoundingClientRect().left - track.getBoundingClientRect().left + track.scrollLeft;
  }

  /**
   * @returns {HTMLElement[]}
   */
  #getCards() {
    const track = this.#track;
    if (!(track instanceof HTMLElement)) return [];

    return [...track.querySelectorAll('.cart-drawer-discover__card')].filter(
      (card) => card instanceof HTMLElement
    );
  }

  /**
   * @returns {ScrollBehavior}
   */
  #scrollBehavior() {
    return prefersReducedMotion() ? 'auto' : 'smooth';
  }

  #updateControls() {
    const track = this.#track;
    const arrows = this.#arrows;
    const prev = this.#prev;
    const next = this.#next;

    if (!(track instanceof HTMLElement) || !(arrows instanceof HTMLElement)) return;
    if (!(prev instanceof HTMLButtonElement) || !(next instanceof HTMLButtonElement)) return;

    const { scrollLeft, scrollWidth, clientWidth } = track;
    const hasOverflow = scrollWidth > clientWidth + SCROLL_EDGE_EPSILON;

    arrows.hidden = !hasOverflow;
    arrows.setAttribute('aria-hidden', hasOverflow ? 'false' : 'true');

    if (!hasOverflow) {
      prev.disabled = true;
      next.disabled = true;
      return;
    }

    prev.disabled = this.#getActiveIndex() <= 0;
    next.disabled = scrollLeft + clientWidth >= scrollWidth - SCROLL_EDGE_EPSILON;
  }
}

if (!customElements.get('cart-drawer-discover-scroll')) {
  customElements.define('cart-drawer-discover-scroll', CartDrawerDiscoverScroll);
}
